/**
 * WOBundleModal — Export the current WO's folder (job + quotes + COs +
 * album link) as JSON, or import a JSON bundle exported from another
 * environment. This is how you carry a fully-configured project from
 * preview to production without re-typing.
 *
 * Export button downloads a .json file AND copies to clipboard.
 * Import accepts pasted JSON OR a .json file, then creates fresh records
 * on THIS environment with new IDs but every value preserved.
 */
import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Download, Upload, ArrowsClockwise, Sparkle, CheckCircle, Warning, Copy,
} from "@phosphor-icons/react";

export default function WOBundleModal({ jobId, wo, onClose, onImported }) {
  const [tab, setTab]         = useState("export");
  const [bundle, setBundle]   = useState(null);
  const [pasted, setPasted]   = useState("");
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState(null);
  const [replace, setReplace] = useState(true);

  const doExport = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/export-bundle`);
      setBundle(data);
      const json = JSON.stringify(data, null, 2);
      // Auto-download
      const blob = new Blob([json], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `wo_bundle_${data.source_wo_number || jobId}.json`;
      a.click();
      // Also copy
      try { await navigator.clipboard.writeText(json); toast.success("Downloaded & copied to clipboard"); }
      catch { toast.success("Bundle downloaded"); }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Export failed")); }
    finally { setBusy(false); }
  };

  const copyBundle = async () => {
    if (!bundle) return;
    try { await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2)); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const onFile = async (file) => {
    if (!file) return;
    try { setPasted(await file.text()); }
    catch { toast.error("Couldn't read file"); }
  };

  const doImport = async () => {
    let payload;
    try { payload = JSON.parse(pasted); }
    catch { toast.error("Invalid JSON — paste the bundle content"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import-wo-bundle", {
        bundle: payload,
        replace_if_exists: replace,
      });
      setResult(data);
      toast.success(`Imported${data.was_existing ? " (updated existing WO)" : " (new WO)"}`);
      onImported?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-[85] flex items-start justify-center p-2 sm:p-4"
      data-testid="wo-bundle-modal">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] flex flex-col border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] overflow-hidden">
        <div className="p-3 bg-gradient-to-r from-teal-700 to-emerald-700 text-white flex items-start justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
              <Sparkle weight="fill" size={11}/> Preview ↔ Production Sync
            </div>
            <div className="font-black uppercase tracking-widest text-lg">Copy WO Folder</div>
            <div className="text-[11px] opacity-90">{wo?.property_address}{wo?.work_order_number ? ` · WO ${wo.work_order_number}` : ""}</div>
          </div>
          <button onClick={onClose} data-testid="wo-bundle-close" className="p-1 hover:bg-black/20"><X size={20}/></button>
        </div>

        <div className="flex border-b-2 border-slate-200">
          <button onClick={() => setTab("export")} data-testid="wo-bundle-tab-export"
            className={`flex-1 py-2 text-xs font-black uppercase tracking-widest ${tab === "export" ? "bg-teal-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Download weight="bold" size={12} className="inline -mt-0.5 mr-1"/> Export from here
          </button>
          <button onClick={() => setTab("import")} data-testid="wo-bundle-tab-import"
            className={`flex-1 py-2 text-xs font-black uppercase tracking-widest ${tab === "import" ? "bg-emerald-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Upload weight="bold" size={12} className="inline -mt-0.5 mr-1"/> Import to here
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {tab === "export" ? (
            <div className="space-y-3">
              <div className="rounded-md bg-teal-50 border-2 border-teal-200 p-3 text-[12px] text-teal-900">
                Downloads a JSON bundle of this WO (job details + every quote + every change order + album link).
                Photos aren't included (they live in this env's storage). On the other environment, open{" "}
                <b>/admin/import-wo</b> and paste the JSON to recreate the folder.
              </div>
              <button onClick={doExport} disabled={busy} data-testid="wo-bundle-export-btn"
                className="w-full h-12 bg-gradient-to-r from-teal-600 to-emerald-700 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
                <Download weight="fill" size={16}/> {busy ? "Exporting…" : "Export & Download Bundle"}
              </button>
              {bundle && (
                <>
                  <div className="rounded-md bg-emerald-50 border-2 border-emerald-200 p-3 text-[11px] text-emerald-900 flex items-start gap-2">
                    <CheckCircle weight="fill" size={16} className="shrink-0 mt-0.5"/>
                    <div>
                      <div className="font-black uppercase text-[10px] tracking-widest mb-1">Bundle contents</div>
                      <div>1 job · {bundle.quotes?.length || 0} quote(s) · {bundle.change_orders?.length || 0} change order(s) · {bundle.photo_album ? "album link" : "no album"}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={copyBundle} className="flex-1 h-10 border-2 border-teal-300 text-teal-700 text-xs font-black uppercase tracking-widest inline-flex items-center justify-center gap-1">
                      <Copy weight="bold" size={12}/> Copy JSON
                    </button>
                  </div>
                  <details className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[10px]">
                    <summary className="cursor-pointer font-bold text-slate-600">Preview JSON</summary>
                    <pre className="mt-1 whitespace-pre-wrap text-slate-700 max-h-48 overflow-y-auto">
                      {JSON.stringify(bundle, null, 2).slice(0, 3000)}
                      {JSON.stringify(bundle, null, 2).length > 3000 ? "\n... (truncated)" : ""}
                    </pre>
                  </details>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md bg-emerald-50 border-2 border-emerald-200 p-3 text-[12px] text-emerald-900">
                Paste a JSON bundle (or upload the file) exported from another environment.
                Creates the WO + all quotes + all COs + album link here.
              </div>
              <input type="file" accept="application/json,.json"
                onChange={e => onFile(e.target.files?.[0])}
                className="w-full text-xs" data-testid="wo-bundle-file"/>
              <textarea value={pasted} onChange={e => setPasted(e.target.value)}
                rows={10} placeholder='{"bundle_version":1,"job":...}'
                className="w-full p-2 border-2 border-slate-300 text-xs font-mono outline-none"
                data-testid="wo-bundle-paste"/>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)}/>
                Overwrite child docs if the WO number already exists (recommended)
              </label>
              <button onClick={doImport} disabled={busy || !pasted.trim()}
                data-testid="wo-bundle-import-btn"
                className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-700 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
                <Upload weight="fill" size={16}/> {busy ? "Importing…" : "Import Bundle"}
              </button>
              {result && (
                <div className="rounded-md bg-emerald-50 border-2 border-emerald-300 p-3 text-[12px] text-emerald-900">
                  <div className="font-black uppercase text-[10px] tracking-widest mb-1 flex items-center gap-1">
                    <CheckCircle weight="fill" size={12}/> Success
                  </div>
                  WO {result.work_order_number} — {result.was_existing ? "updated existing" : "created new"} · {result.quotes_imported} quote(s) · {result.change_orders_imported} change order(s){result.album_token ? " · album minted" : ""}
                  <div className="mt-1 text-[10px] font-mono text-emerald-800">job_id: {result.job_id}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
